export const ASSIGNMENT_STATUSES = [
  'pending',
  'accepted',
  'rejected',
  'auto_rejected',
  'withdrawn',
  'completed',
] as const

export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number]

const ASSIGNMENT_STATUS_SET = new Set<string>(ASSIGNMENT_STATUSES)

export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  rejected: 'Rejected',
  auto_rejected: 'Auto Rejected',
  withdrawn: 'Withdrawn',
  completed: 'Completed',
}

export const LEGACY_ASSIGNMENT_STATUS_ALIASES: Partial<Record<string, AssignmentStatus>> = {
  assigned: 'pending',
  confirmed: 'accepted',
  declined: 'rejected',
  in_progress: 'accepted',
  cancelled: 'rejected',
}

export const ACTIVE_ASSIGNMENT_STATUSES = new Set<AssignmentStatus>(['pending', 'accepted'])

export const INACTIVE_ASSIGNMENT_STATUSES = new Set<AssignmentStatus>([
  'rejected',
  'auto_rejected',
  'withdrawn',
  'completed',
])

export function resolveAssignmentStatus(raw?: string | null): AssignmentStatus | null {
  const value = String(raw ?? '').trim().toLowerCase()
  if (!value) {
    return null
  }

  const legacy = LEGACY_ASSIGNMENT_STATUS_ALIASES[value]
  if (legacy) {
    return legacy
  }

  if (ASSIGNMENT_STATUS_SET.has(value)) {
    return value as AssignmentStatus
  }

  return null
}

export function getAssignmentStatusLabel(status?: string | null): string {
  const resolved = resolveAssignmentStatus(status)
  if (resolved) {
    return ASSIGNMENT_STATUS_LABELS[resolved]
  }

  const raw = String(status ?? '').trim()
  if (!raw) {
    return 'Unknown'
  }

  return raw.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

export function isActiveAssignmentStatus(status?: string | null): boolean {
  const resolved = resolveAssignmentStatus(status)
  return resolved ? ACTIVE_ASSIGNMENT_STATUSES.has(resolved) : false
}

export function isInactiveAssignmentStatus(status?: string | null): boolean {
  const resolved = resolveAssignmentStatus(status)
  return resolved ? INACTIVE_ASSIGNMENT_STATUSES.has(resolved) : false
}

export function isTerminalAssignmentStatus(status?: string | null): boolean {
  return isInactiveAssignmentStatus(status)
}

const ASSIGNMENT_STATUSES_BLOCKING_WORKDAY_ENTRY_STATUS_CHANGE = new Set<AssignmentStatus>([
  'completed',
  'withdrawn',
  'rejected',
  'auto_rejected',
])

export function isAssignmentStatusBlockingWorkDayEntryStatusChange(status?: string | null): boolean {
  const resolved = resolveAssignmentStatus(status)
  return resolved ? ASSIGNMENT_STATUSES_BLOCKING_WORKDAY_ENTRY_STATUS_CHANGE.has(resolved) : false
}

/** Matches backend WORKDAY_ENTRY_ASSIGNMENT_STATUSES in job_mgmt_be/api/views/workday.py */
export const WORKDAY_ENTRY_ASSIGNMENT_STATUS_CODES = new Set([
  'pending',
  'accepted',
  'in_progress',
  'in-progress',
  'scheduled',
])

export function isWorkdayEntryAssignmentStatus(
  status?: string | null,
  workflowStatusCode?: string | null,
): boolean {
  const candidates = [status, workflowStatusCode]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean)

  for (const raw of candidates) {
    if (WORKDAY_ENTRY_ASSIGNMENT_STATUS_CODES.has(raw)) {
      return true
    }

    const resolved = resolveAssignmentStatus(raw)
    if (resolved && WORKDAY_ENTRY_ASSIGNMENT_STATUS_CODES.has(resolved)) {
      return true
    }
  }

  return false
}

type AssignmentWorkerSource = {
  worker_id?: string | number | null
  worker?: string | number | { id?: string | number } | null
  worker_skill?:
    | string
    | number
    | {
        worker?: string | number | { id?: string | number } | null
        worker_id?: string | number | null
      }
    | null
}

export function resolveAssignmentWorkerId(
  assignment?: AssignmentWorkerSource | null,
): string {
  const direct = assignment?.worker_id ?? assignment?.worker
  if (direct && typeof direct === 'object' && 'id' in direct) {
    const nestedId = String(direct.id ?? '').trim()
    if (nestedId) {
      return nestedId
    }
  }

  const directId = String(direct ?? '').trim()
  if (directId) {
    return directId
  }

  const workerSkill = assignment?.worker_skill
  if (workerSkill && typeof workerSkill === 'object') {
    const skillWorker = workerSkill.worker
    if (skillWorker && typeof skillWorker === 'object' && 'id' in skillWorker) {
      const nestedWorkerId = String(skillWorker.id ?? '').trim()
      if (nestedWorkerId) {
        return nestedWorkerId
      }
    }

    const skillWorkerId = workerSkill.worker_id ?? skillWorker
    const skillWorkerIdValue = String(skillWorkerId ?? '').trim()
    if (skillWorkerIdValue) {
      return skillWorkerIdValue
    }
  }

  return ''
}

export type WorkDayEntryAssignmentDedupRecord = {
  assignment?: string | number | null
  assignment_id?: string | number | null
  worker?: string | number | null
}

export function isAssignmentAlreadyOnWorkDay(
  assignment: AssignmentWorkerSource & { id?: string | number },
  entries: readonly WorkDayEntryAssignmentDedupRecord[],
): boolean {
  const assignmentId = String(assignment.id ?? '').trim()
  const workerId = resolveAssignmentWorkerId(assignment)

  if (assignmentId) {
    const hasEntryForAssignment = entries.some((entry) => {
      const entryAssignmentId = String(entry.assignment_id ?? entry.assignment ?? '').trim()
      return entryAssignmentId === assignmentId
    })
    if (hasEntryForAssignment) {
      return true
    }
  }

  if (workerId) {
    const hasUnlinkedWorkerEntry = entries.some((entry) => {
      const entryWorkerId = String(entry.worker ?? '').trim()
      const entryAssignmentId = String(entry.assignment_id ?? entry.assignment ?? '').trim()
      return entryWorkerId === workerId && !entryAssignmentId
    })
    if (hasUnlinkedWorkerEntry) {
      return true
    }
  }

  return false
}

type AssignmentAddableRecord = AssignmentWorkerSource & {
  id?: string | number
  status?: string | null
  workflow_status_code?: string | null
}

export function isAssignmentAddableToWorkDay(
  assignment: AssignmentAddableRecord,
  entries: readonly WorkDayEntryAssignmentDedupRecord[],
): boolean {
  if (!isWorkdayEntryAssignmentStatus(assignment.status, assignment.workflow_status_code)) {
    return false
  }

  if (!resolveAssignmentWorkerId(assignment)) {
    return false
  }

  return !isAssignmentAlreadyOnWorkDay(assignment, entries)
}

type AssignmentLookupRecord = {
  id?: string | number
  job_id?: string | number
  worker_id?: string | number | null
  status?: string | null
}

type AssignmentDeleteRecord = {
  worker_id?: string | number | null
  status?: string | null
}

export function canDeleteAssignment(assignment?: AssignmentDeleteRecord | null): boolean {
  if (!assignment) {
    return false
  }

  const workerId = String(assignment.worker_id ?? '').trim()
  if (!workerId) {
    return true
  }

  return resolveAssignmentStatus(assignment.status) === 'pending'
}

export function getAssignmentDeleteBlockedMessage(): string {
  return 'Only unassigned assignments or assignments still in assigned/pending status can be deleted.'
}

function compareAssignmentRecency(
  left: AssignmentLookupRecord,
  right: AssignmentLookupRecord,
): number {
  return Number(right.id ?? 0) - Number(left.id ?? 0)
}

export function buildLatestAssignmentsByJobWorkerKey<T extends AssignmentLookupRecord>(
  assignments: readonly T[],
): Map<string, T> {
  const map = new Map<string, T>()
  const sorted = [...assignments].sort(compareAssignmentRecency)

  for (const assignment of sorted) {
    const workerId = String(assignment.worker_id ?? '').trim()
    const jobId = String(assignment.job_id ?? '').trim()
    if (!workerId || !jobId) {
      continue
    }

    const key = `${jobId}:${workerId}`
    if (!map.has(key)) {
      map.set(key, assignment)
    }
  }

  return map
}

export function buildLatestAssignmentsByWorkerId<T extends AssignmentLookupRecord>(
  assignments: readonly T[],
): Map<string, T> {
  const map = new Map<string, T>()
  const sorted = [...assignments].sort(compareAssignmentRecency)

  for (const assignment of sorted) {
    const workerId = String(assignment.worker_id ?? '').trim()
    if (!workerId || map.has(workerId)) {
      continue
    }

    map.set(workerId, assignment)
  }

  return map
}

export function buildLatestAssignmentsByJobId<T extends AssignmentLookupRecord>(
  assignments: readonly T[],
): Map<string, T> {
  const map = new Map<string, T>()
  const sorted = [...assignments].sort(compareAssignmentRecency)

  for (const assignment of sorted) {
    const jobId = String(assignment.job_id ?? '').trim()
    if (!jobId || map.has(jobId)) {
      continue
    }

    map.set(jobId, assignment)
  }

  return map
}

export function getAssignmentEditBlockedMessage(status?: string | null): string {
  const label = getAssignmentStatusLabel(status)
  return `This assignment is ${label} and can no longer be edited.`
}

export function getWorkdayEntryBlockedMessage(status?: string | null): string {
  const label = getAssignmentStatusLabel(status)
  return `Work day entries cannot be added or edited for ${label} assignments.`
}

export function getAssignmentStatusBadgeClass(status?: string | null): string {
  switch (resolveAssignmentStatus(status)) {
    case 'pending':
      return 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200'
    case 'accepted':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200'
    case 'completed':
      return 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200'
    case 'withdrawn':
      return 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
    case 'auto_rejected':
      return 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'
    case 'rejected':
      return 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200'
    default:
      return 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
  }
}

export function getAssignmentStatusBadgeVariant(
  status?: string | null,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (resolveAssignmentStatus(status)) {
    case 'pending':
      return 'secondary'
    case 'accepted':
    case 'completed':
      return 'default'
    case 'rejected':
    case 'auto_rejected':
      return 'destructive'
    case 'withdrawn':
      return 'outline'
    default:
      return 'secondary'
  }
}
